import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { PHCOnboardingActions } from "stores/PHC/PHCOnboarding/PHCOnboardingAction";
import { PHCOnboardingApis } from "stores/PHC/PHCOnboarding/PHCOnboardingApis";
import PHCGenerateReport from "../GenerateReport/PHCGenerateReport";

function PHCLoaderReport() {
  const dispatch = useDispatch();
  const { portfolioId } = useSelector((state) => state.PHC.PHCOnboarding);
  const [isloading, setLoading] = useState(false);

  const analysisSteps = [
    {
      title: "Searching Stock Database",
      subtitle: "Querying ticker symbols and historical data",
      icon: "🔍",
    },
    {
      title: "Fetching Market Data",
      subtitle: "Retrieving real-time quotes and volume",
      icon: "📊",
    },
    {
      title: "Analyzing Price Movements",
      subtitle: "Calculating trends and volatility metrics",
      icon: "📈",
    },
    {
      title: "Evaluating Technical Indicators",
      subtitle: "RSI, MACD, Moving Averages analysis",
      icon: "⚙️",
    },
    {
      title: "Generating Trading Signals",
      subtitle: "Identifying buy/sell opportunities",
      icon: "🚦",
    },
    {
      title: "Building Portfolio Insights",
      subtitle: "Creating risk/reward assessment",
      icon: "📋",
    },
    {
      title: "Finalizing Health Report",
      subtitle: "Compiling comprehensive analysis",
      icon: "✅",
    },
  ];

  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const payload = { portfolioId };

    // Simulate progress
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 100;
        return prev + 1.5;
      });
    }, 100);

    // Rotate analysis steps
    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % analysisSteps.length);
    }, 2000);

    // Call API after 10 seconds
    const timer = setTimeout(async () => {
      try {
        await PHCOnboardingApis.phcHealthGenerate(payload);
        setLoading(true);
        dispatch(PHCOnboardingActions.getOnboardingData());
      } catch (err) {
        console.error("Health Generate Error:", err);
      }
    }, 10000);

    return () => {
      clearTimeout(timer);
      clearInterval(stepInterval);
      clearInterval(progressInterval);
    };
  }, []);

  return (
    <>
      {isloading ? (
        <>
          <PHCGenerateReport />
        </>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",

            padding: "20px",
            fontFamily: "'Segoe UI', 'Roboto', sans-serif",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "40px",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
              maxWidth: "600px",
              width: "100%",
              border: "1px solid #e2e8f0",
            }}
          >
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: "32px" }}>
              <p
                style={{
                  color: "#64748b",
                  fontSize: "16px",
                  margin: 0,
                  lineHeight: "1.5",
                }}
              >
                Analyzing your portfolio performance and generating insights
              </p>
            </div>

            {/* Current Analysis Step */}
            <div
              style={{
                backgroundColor: "#f8fafc",
                borderRadius: "12px",
                padding: "24px",
                marginBottom: "24px",
                border: "1px solid #e2e8f0",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: "linear-gradient(90deg, #1e88e5, transparent)",
                  animation: "scan 2s linear infinite",
                }}
              />

              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <div
                  style={{
                    fontSize: "32px",
                    animation: "pulse 2s infinite",
                  }}
                >
                  {analysisSteps[currentStep].icon}
                </div>
                <div>
                  <h3
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#1e293b",
                    }}
                  >
                    {analysisSteps[currentStep].title}
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      color: "#64748b",
                      fontSize: "14px",
                      lineHeight: "1.4",
                    }}
                  >
                    {analysisSteps[currentStep].subtitle}
                  </p>
                </div>
              </div>
            </div>

            {/* Data Flow Visualization */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                marginBottom: "32px",
              }}
            >
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor: i % 2 === 0 ? "#1e88e5" : "#0d47a1",
                    opacity:
                      0.3 + ((Math.sin(Date.now() / 500 + i) + 1) / 2) * 0.7,
                    animation: `dataFlow 1.5s infinite ${i * 0.2}s`,
                  }}
                />
              ))}
              <div
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  marginLeft: "12px",
                  fontWeight: "500",
                }}
              >
                Streaming market data...
              </div>
            </div>

            {/* Loading Indicator */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
              }}
            >
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  border: "3px solid #e0e0e0",
                  borderTop: "3px solid #1e88e5",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
              <span
                style={{
                  color: "#475569",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Please wait while we analyze your portfolio
              </span>
            </div>
          </div>

          {/* Bottom Note */}
          <div
            style={{
              marginTop: "24px",
              textAlign: "center",
              color: "#94a3b8",
              fontSize: "13px",
              maxWidth: "600px",
            }}
          >
            This may take a moment as we process real-time market data and
            perform comprehensive analysis
          </div>

          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              
              @keyframes pulse {
                0%, 100% { opacity: 1; transform: scale(1); }
                50% { opacity: 0.8; transform: scale(1.05); }
              }
              
              @keyframes scan {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(100%); }
              }
              
              @keyframes dataFlow {
                0%, 100% { transform: translateY(0); opacity: 0.3; }
                50% { transform: translateY(-10px); opacity: 1; }
              }
            `}
          </style>
        </div>
      )}
    </>
  );
}

export default PHCLoaderReport;
